import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, 
  DollarSign, 
  Building2, 
  Target,
  BarChart3,
  PieChart,
  Activity,
  Users,
  Briefcase,
  AlertTriangle,
  CheckCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Filter
} from 'lucide-react';
import { useVentureData } from '@/hooks/useVentureData';
import { VentureSignalSection } from './VentureSignalSection';
import { VentureDealsOverview } from './VentureDealsOverview';
import { VentureInviteTab } from './VentureInviteTab';

const VentureCapitalDashboard: React.FC = () => {
  const { ventureData, loading, error } = useVentureData();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-20 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded" />
          ))}
        </div>
        <div className="h-96 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (error || !ventureData?.venture_capital) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">
            {error || 'Please complete your VC profile setup to access the dashboard.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const vc = ventureData.venture_capital;
  const formatCurrency = (amount: number) => {
    if (amount >= 1000000000) return `$${(amount / 1000000000).toFixed(1)}B`;
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount}`;
  };

  // Mock data for portfolio and deals - replace with real data hooks
  const portfolioCompanies = [
    { id: 1, name: 'TechCorp', sector: 'SaaS', stage: 'Series B', investment: 5000000, currentValue: 8500000, status: 'growing' },
    { id: 2, name: 'AI Dynamics', sector: 'AI/ML', stage: 'Series A', investment: 3000000, currentValue: 4200000, status: 'stable' },
    { id: 3, name: 'FinTech Solutions', sector: 'FinTech', stage: 'Seed', investment: 1000000, currentValue: 1800000, status: 'growing' },
    { id: 4, name: 'HealthTech', sector: 'Healthcare', stage: 'Series C', investment: 10000000, currentValue: 15000000, status: 'growing' }
  ];

  const dealPipeline = [
    { id: 1, company: 'NextGen Analytics', sector: 'Data', stage: 'Due Diligence', amount: 7500000, probability: 75 },
    { id: 2, company: 'CloudSpace', sector: 'Infrastructure', stage: 'Investment Committee', amount: 12000000, probability: 60 },
    { id: 3, company: 'BioInnovate', sector: 'Biotech', stage: 'Sourcing', amount: 5000000, probability: 30 },
    { id: 4, company: 'EduTech Pro', sector: 'EdTech', stage: 'Term Sheet', amount: 3500000, probability: 85 }
  ];

  const totalPortfolioValue = portfolioCompanies.reduce((sum, company) => sum + company.currentValue, 0);
  const totalInvested = portfolioCompanies.reduce((sum, company) => sum + company.investment, 0);
  const totalROI = ((totalPortfolioValue - totalInvested) / totalInvested) * 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {vc.firm_name || ventureData.display_name} Dashboard
          </h1>
          <p className="text-muted-foreground">
            Portfolio overview and investment analytics
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button size="sm">
            <Calendar className="h-4 w-4 mr-2" />
            Schedule
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">AUM</p>
                <p className="text-2xl font-bold text-foreground">
                  {vc.aum ? formatCurrency(Number(vc.aum)) : '$0'}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Portfolio Value</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(totalPortfolioValue)}
                </p>
                <div className="flex items-center mt-1">
                  <ArrowUpRight className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-green-500 ml-1">+{totalROI.toFixed(1)}%</span>
                </div>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Deals</p>
                <p className="text-2xl font-bold text-foreground">{dealPipeline.length}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatCurrency(dealPipeline.reduce((sum, deal) => sum + deal.amount, 0))} pipeline
                </p>
              </div>
              <Briefcase className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Portfolio Companies</p>
                <p className="text-2xl font-bold text-foreground">{vc.portfolio_companies || portfolioCompanies.length}</p>
                <p className="text-xs text-green-500 mt-1">
                  {portfolioCompanies.filter(c => c.status === 'growing').length} growing
                </p>
              </div>
              <Building2 className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Dashboard Tabs */}
        <Tabs defaultValue="deals" className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="invite">Invite</TabsTrigger>
            <TabsTrigger value="portfolio">Portfolio Overview</TabsTrigger>
            <TabsTrigger value="deals">Deal Management</TabsTrigger>
            <TabsTrigger value="pipeline">Deal Pipeline</TabsTrigger>
            <TabsTrigger value="intelligence">Market Intelligence</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

        <TabsContent value="invite" className="space-y-6">
          <VentureInviteTab />
        </TabsContent>

        <TabsContent value="portfolio" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Portfolio Companies */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Portfolio Companies
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {portfolioCompanies.map((company) => (
                    <div key={company.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{company.name}</h4>
                          <Badge variant="outline">{company.sector}</Badge>
                          <Badge variant="secondary">{company.stage}</Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span>Invested: {formatCurrency(company.investment)}</span>
                          <span>Current: {formatCurrency(company.currentValue)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                          company.status === 'growing' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {company.status === 'growing' ? <TrendingUp className="h-3 w-3" /> : <Activity className="h-3 w-3" />}
                          {company.status}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Portfolio Allocation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Portfolio Allocation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">By Sector</h4>
                    <div className="space-y-2">
                      {['SaaS', 'AI/ML', 'FinTech', 'Healthcare'].map((sector, index) => (
                        <div key={sector} className="flex items-center justify-between">
                          <span className="text-sm">{sector}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary rounded-full" 
                                style={{ width: `${(index + 1) * 20}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground w-8">
                              {(index + 1) * 20}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">By Stage</h4>
                    <div className="space-y-2">
                      {['Seed', 'Series A', 'Series B', 'Series C'].map((stage, index) => (
                        <div key={stage} className="flex items-center justify-between">
                          <span className="text-sm">{stage}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-500 rounded-full" 
                                style={{ width: `${(4 - index) * 15}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground w-8">
                              {(4 - index) * 15}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          </TabsContent>

          <TabsContent value="deals" className="space-y-6">
            <VentureDealsOverview />
          </TabsContent>

          <TabsContent value="pipeline" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Deal Pipeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dealPipeline.map((deal) => (
                  <div key={deal.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{deal.company}</h4>
                        <Badge variant="outline">{deal.sector}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span>Amount: {formatCurrency(deal.amount)}</span>
                        <span>Stage: {deal.stage}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-sm font-medium">{deal.probability}%</div>
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full" 
                            style={{ width: `${deal.probability}%` }}
                          />
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="intelligence" className="space-y-6">
          <VentureSignalSection />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Deployed Capital</span>
                    <span className="font-medium">{formatCurrency(totalInvested)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Current Portfolio Value</span>
                    <span className="font-medium">{formatCurrency(totalPortfolioValue)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Unrealized Gains</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(totalPortfolioValue - totalInvested)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Portfolio IRR</span>
                    <span className="font-medium">24.5%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">MOIC</span>
                    <span className="font-medium">2.3x</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Fund Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Fund Size</span>
                    <span className="font-medium">{vc.aum ? formatCurrency(Number(vc.aum)) : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Deployed Capital</span>
                    <span className="font-medium">68%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Dry Powder</span>
                    <span className="font-medium">
                      {vc.aum ? formatCurrency(Number(vc.aum) * 0.32) : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Active Investments</span>
                    <span className="font-medium">{portfolioCompanies.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Avg Check Size</span>
                    <span className="font-medium">
                      {formatCurrency(totalInvested / portfolioCompanies.length)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VentureCapitalDashboard;